<template>
  <base-modal hide-header hide-footer size="lg" @close="$emit('close')">
    <div class="import-wizard-body">
      <div class="import-wizard-content">
        <div class="col-12" @click.stop="">
        <!-- Tabs for browsing -->
        <ul class="nav nav-tabs routa-tabs" role="tablist">
            <li class="nav-item">
                <button
                    class="routa-tab"
                    :class="{ active: tabIndex === 0 }"
                    :style="linkTabClass(0)"
                    @click="tabIndex = 0"
                >
                    {{ $t('import_file_wizard.import_geojson_file') }}
                </button>
                <div class="routa-tab-indicator"/>
            </li>
            <li v-if="!showOnlyGeoJsonTab" class="nav-item">
                <button
                    class="routa-tab"
                    :class="{ active: tabIndex === 1 }"
                    :style="linkTabClass(1)"
                    @click="tabIndex = 1"
                >
                    {{ $t('import_file_wizard.import_shape_file') }}
                </button>
                <div class="routa-tab-indicator"/>
            </li>
        </ul>
        <!-- Tab contents -->
        <div class="tab-content routa-tab-content">
            <div v-show="tabIndex === 0" class="tab-pane fade show active">
                <geojson-file-picker
                    :label="$t('import_file_wizard.GeoJson_file_attachment_placeholder')"
                    :extension="getGeoJsonFileExtension()"
                    @input="geojsonFilePicked"
                />
            </div>
            <div v-show="tabIndex === 1" class="tab-pane fade show active">
                <shape-import-form @file-picked="shapeFileSelected" />
            </div>
        </div>
      </div>

      <div class="col-sm-12 nopads">
        <div>
          <div v-if="crs_selection_visible">
            <span class="span-title">{{ $t('import_file_wizard.choose_file_CRS') }}</span>
              <select
                  v-model="crs"
                  class="form-select form-select-sm"
              >
                  <option :value="null" disabled>
                      {{ $t('import_file_wizard.choose_file_CRS') }}
                  </option>
                  <option
                      v-for="item in getCRSTypes()"
                      :key="item.value"
                      :value="item.value"
                  >
                      {{ item.name }}
                  </option>
              </select>
          </div>
          <div v-else-if="tabIndex === 0">
              <span class="span-title pl-2">{{ $t('import_file_wizard.use_coordinate_system') }}: WGS84</span>
          </div>
          <div v-else-if="tabIndex === 1">
            <span class="span-title pl-2">{{ $t('import_file_wizard.use_coordinate_system') }}: ETRS-TM35FIN (epsg: 3067)</span>
          </div>
        </div>
        <div v-if="fileReady" class="col-sm-12 nopads py-1 import-wizard-area-counts">
            <span class="text-success fw-bold me-2">{{ $t('import_file_wizard.valid_area_count') }}:</span>
            <span class="text-body">{{ validAreaCount }}</span>
            <span class="text-danger fw-bold ms-4 me-2">{{ $t('import_file_wizard.rejected_area_count') }}:</span>
            <span class="text-body">{{ rejectedAreaCount }}</span>
        </div>
        <div v-if="fileReady && rejectedAreaCount > 0" class="info-box mt-2">
            <font-awesome-icon icon="info-circle" class="info-circle" />
            <span>{{ $t('import_file_wizard.rejected_area_info') }}</span>
        </div>
        <div class="col-sm-12 nopads my-2">
          <file-import-preview
            ref="shapePreviewControl"
            v-model:coordinate="coordinateModel"
            :point-type="0"
            :coordinate="coordinate"
          />
        </div>
        <div
          v-if="loading"
          id="loader"
          class="spinner"
        />
      </div>
    </div>

    <div class="import-wizard-footer">
      <button
        class="btn btn-secondary form-button"
        @click.stop="$emit('close')"
      >
        {{ $t('common.cancel') }}
      </button>
      <button
        class="btn btn-success form-button"
        :disabled="loading || !allFilesGiven || !fileReady"
        @click.stop="submit"
      >
        {{ $t('import_file_wizard.import_file') }}
      </button>
    </div>
    </div>
  </base-modal>
</template>

<script>
import BaseModal from '../BaseModal'
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import FileImportPreview from "./FileImportPreview";
import {mapHelper} from '../mixins/MapMixin'
import {restApi} from '../mixins/RestApiMixin'
import {geometryMixin} from '../mixins/GeometryMixin'
import GeojsonFilePicker from "./GeojsonFilePicker";
import ShapeImportForm from "./ShapeImportForm";
import {inject} from "vue";

// From: https://epsg.io/?q=Finland
export const CRSTypes = {
    Epsg3067: {EpsgCode: 3067, title: 'ETRS89 / TM35FIN(E,N)'},
    Epsg4326: {EpsgCode: 4326, title: 'WGS 84 - WGS84'},
}

export default {
    name: 'ImportFileWizard' ,
    components: {ShapeImportForm, GeojsonFilePicker, BaseModal, FileImportPreview},
    mixins: [
        attachmentHelper,
        mapHelper,
        restApi,
        geometryMixin
    ],
    props: {
        coordinate: {
            type: Object,
            default: null
        },
        showOnlyGeoJsonTab: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            loading: false,
            allFilesGiven: false,
            fileReady: false,
            tabIndex: 0,
            shpFile: null,
            shxFile: null,
            dbfFile: null,
            geojsonFile: null,
            geojson: null,
            crs: '',
            crs_selection_visible: false,
            coordinateModel: null,
            validAreaCount: 0,
            rejectedAreaCount: 0
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted: function () {
        this.coordinateModel = this.coordinate
    },
    watch: {
        'shpFile': {
            handler: function (newValue) {
                this.allFilesGiven = (newValue !== null && this.dbfFile !== null && this.shxFile !== null)
           }
        },
        'shxFile': {
            handler: function (newValue) {
                this.allFilesGiven = (this.shpFile !== null && this.dbfFile !== null && newValue !== null)
            }
        },
        'dbfFile': {
            handler: function (newValue) {
                this.allFilesGiven = (this.shpFile !== null && newValue !== null && this.shxFile !== null)
            }
        },
        'geojsonFile': {
            handler: function (newValue) {
                this.allFilesGiven = (this.geojsonFile !== null && newValue !== null)
            }
        },
        allFilesGiven(val) {
            if (val) {
                this.processGeoFileToGeoJson()
            }
        }
    },
    methods: {
        getFileName() {
            const file = this.tabIndex === 0 ? this.geojsonFile : this.shpFile
            return file ? file.name.split('.')[0] : null
        },
        submit: function () {
            // Load shape on preview map to main map
            this.$emit('addLayer', this.$refs.shapePreviewControl.getGeoJson(), this.getFileName() )
        },
        getCRSTypes: function () {
            return [
                {name: CRSTypes.Epsg3067.title, value: CRSTypes.Epsg3067.EpsgCode},
                {name: CRSTypes.Epsg4326.title, value: CRSTypes.Epsg4326.EpsgCode}
            ]
        },
        getShpFileExtension:function (){
            return ".shp"
        },
        getShxFileExtension: function (){
            return ".shx"
        },
        getDbfFileExtension: function (){
            return ".dbf"
        },
        getGeoJsonFileExtension: function (){
            return ".GeoJson"
        },
        processGeoFileToGeoJson: function () {
            let formData = new FormData()
            if (this.tabIndex === 1) {
                // SHAPE - Have to give files in this order
                let files = [this.shpFile, this.shxFile, this.dbfFile]
                files.forEach(file => {
                    if (file !== undefined && file !== null) {
                        formData.append('files[]', file)
                    }
                })
                this.loading = true
                this.restPostWithFile(this.convertShapeToGeoJsonUrl, {}, formData, this.handleGeoJson, this.onFailedRequest)
            } else if (this.tabIndex === 0) {
                // GeoJSON
                this.loading = true;
                this.readGeoJsonFile()
            }
        },
        fileImportFail: function () {
            this.showAlert(this.$t('common.alert_add_failed'))
            this.loading = false
            this.fileReady = false
        },
        readGeoJsonFile: function(){
            let file = this.geojsonFile
            let read = new FileReader();
            read.onload = function() {
                this.geojson = read.result
                try {
                    JSON.parse(this.geojson)
                } catch (e) {
                    this.fileImportFail()
                    return;
                }
                this.handleGeoJson()
            }.bind(this)
            read.readAsText(file);
        },
        handleGeoJson: function(file){
            if(!file){
                file = this.geojson
            }

            let validation
            try {
                validation = this.getGeoJsonBoundsValidation(file)
            } catch (e) {
                this.loading = false
                this.fileReady = false
                this.showAlert(this.$t('import_file_wizard.invalid_geometry_error'))
                return
            }

            if (validation.hasMalformedData) {
                this.loading = false
                this.fileReady = false
                this.showAlert(this.$t('import_file_wizard.invalid_geometry_error'))
                return
            }

            if (validation.validCount === 0) {
                this.loading = false
                this.fileReady = false
                this.showAlert(this.$t('import_file_wizard.all_features_outside_area_error'))
                return
            }

            this.validAreaCount = validation.validCount
            this.rejectedAreaCount = validation.rejectedCount
            this.$refs.shapePreviewControl.renderGeoJson(JSON.stringify(validation.geoJson))
            this.loading = false
            this.fileReady = true
        },
        onFailedRequest: function(){
            this.loading = false
            this.fileReady = false
            this.showAlert(this.$t('import_file_wizard.import_failed'))
        },
        shapeFileSelected: function (shpFile, shxFile, dbfFile) {
            this.validAreaCount = 0
            this.rejectedAreaCount = 0
            this.shpFile = shpFile
            this.shxFile = shxFile
            this.dbfFile = dbfFile
            this.allFilesGiven = (this.shpFile !== null && this.shxFile !== null && this.dbfFile !== null)
        },
        geojsonFilePicked: function(event){
            this.allFilesGiven = false
            this.validAreaCount = 0
            this.rejectedAreaCount = 0
            this.$nextTick(() =>  {
                this.geojsonFile = event.target.files[0]
                this.allFilesGiven = true
            })
        },
        linkTabClass(index) {
            if (this.tabIndex === index) {
                return ['tab-title', 'tab-title-active']
            } else {
                return ['tab-title']
            }
        }
    }
}
</script>
<style>
@import "../../styles/modal.css";
</style>
<style scoped>
.import-wizard-body {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}
.import-wizard-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.import-wizard-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: .5em;
  border-top: 1px solid #dee2e6;
  margin-top: .5em;
}
.info-box {
  padding: 0.5rem .5rem !important;
}
</style>
